import {FC, useEffect, useMemo, useRef, useState} from 'react'
import clsx from 'clsx'
import {URL, WebSite, isOnline, projectName} from '../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {Invoice, InvoicePayment, InvoicePaymentForm, InvoicePaymentTypeItems} from '../core/_models'
import {InvoicePaymentEditModal} from '../invoice-edit-modal/payment/InvoicePaymentEditModal'
import {getPaymentTaxValidationError} from '../core/paymentTaxValidation'
import {InvoicePrintModel} from '../../invoice-print/core/_models'
import {PX_PER_MM, SHEET_SIZE} from '../../invoice-print/core/printLayout'
import {printLayoutOf} from '../../invoice-print/core/printLayoutSettings'
import {formatNumber, sumNumbers} from '../../invoice-print/core/printNumbers'
import {RowsTotals} from '../../invoice-print/core/printModel'
import {usePrintLayout} from '../../invoice-print/core/usePrintLayout'
import {
  classicColumns,
  classicNoteLines,
  classicPatternFields,
  classicQrValue,
  extraTotalRows,
  grandTotals,
} from './classic/classicPrintModel'
import {ClassicMeasure, ClassicSheets, ClassicShared} from './classic/ClassicDocument'
import {qrLogoImage} from './classic/classicImages'
import {useClassicPrintPage} from './classic/useClassicPrintPage'
import '../../invoice-print/InvoicePrintPage.css'
import './classic/ClassicPrint.css'

type Props = {
  /** The invoice as the panel reads it: payment details, attachments and the QR code's text. */
  invoice: Invoice
  /** The printed invoice (api/public/invoice-print/{hash}): every value the sheets print. */
  model: InvoicePrintModel
  /** On a phone the sheets show only when the full invoice is asked for (the receipt shows otherwise). */
  fullPrint: boolean
  withSign: {
    withSign: string
    setWithSign: (value: string) => void
  }
  /** The server's PDF renderer: the sheets at their own size. */
  pdfMode?: boolean
  /** Told when the sheets are laid out (and when a change lays them out again). */
  onReadyChange?: (ready: boolean) => void
}

const SHEET_GAP_PX = 24

const paymentForm = (payment: InvoicePayment): InvoicePaymentForm => ({
  id: 0,
  price: payment.prc || 0,
  cash: payment.csh || 0,
  vat: payment.vt || 0,
  date: payment.dt || '',
  description: payment.dsc || '',
  type: String(payment.typ || 3),
  refereceNo: payment.trc || '',
  traceNo: payment.trc || '',
  cardNo: payment.cno || '',
  payerId: payment.nid || '',
  payerNationalCode: payment.nid || '',
  switchNumber: payment.swno || payment.switchNumber || '',
  acquirerNumber: payment.aqno || payment.acquirerNumber || '',
  terminalNumber: payment.terno || payment.terminalNumber || '',
  files: payment.fl,
  taxValidationError: payment.txerr || payment.taxValidationError || '',
})

/**
 * The earlier print page (/invoicePrint/{hash}): the invoice in the earlier page's arrangement on A4
 * landscape sheets, laid out and paginated in the browser exactly like the A4 page (usePrintLayout),
 * from the same printed invoice. No row is split between sheets, the column heads repeat on every
 * sheet, and a sheet of rows ends with its page and running totals when the rows take more than one.
 */
const InvoicePrintModalPrint: FC<Props> = ({
  invoice,
  model,
  fullPrint,
  withSign,
  pdfMode,
  onReadyChange,
}) => {
  const settings = useMemo(() => printLayoutOf(model), [model])
  const columns = useMemo(() => classicColumns(model), [model])
  const noteLines = useMemo(() => classicNoteLines(model, invoice.fls), [model, invoice.fls])
  const noteTexts = useMemo(() => noteLines.map((line) => line.text), [noteLines])
  const patternFields = useMemo(() => classicPatternFields(model, invoice), [model, invoice])
  const grand = useMemo(() => grandTotals(model, columns), [model, columns])
  const extraRows = useMemo(() => extraTotalRows(model, columns), [model, columns])
  const showSignature = withSign.withSign === '1'

  const measureRef = useRef<HTMLDivElement>(null)
  const sheetsRef = useRef<HTMLDivElement>(null)
  const layout = usePrintLayout({
    invoice: model,
    columns,
    noteLines: noteTexts,
    choice: 'landscape',
    showSignature,
    measureRef,
    sheetsRef,
  })

  const allRowsTotals = useMemo(() => {
    const totals: RowsTotals = {page: {}, running: {}}
    columns
      .filter((column) => column.sum)
      .forEach((column) => {
        const sum = sumNumbers(model.items.map(column.value))
        totals.page[column.key] = sum
        totals.running[column.key] = sum
      })
    return totals
  }, [model, columns])
  // «جمع کل» prints under the same columns: they are fitted around it too.
  const probeTotals = useMemo(() => [grand, ...layout.probeTotals], [grand, layout.probeTotals])

  const [editPaymentItem, setEditPaymentItem] = useState<InvoicePaymentForm | null | undefined>(
    undefined
  )
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)

  useClassicPrintPage(
    `صورتحساب الکترونیکی - ${model.invoiceNumber || invoice.no || ''}-${model.trackingCode}`
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    onReadyChange?.(layout.ready)
  }, [layout.ready, onReadyChange])

  const shared: ClassicShared = {
    invoice: model,
    columns,
    noteLines,
    patternFields,
    grand,
    extraRows,
    settings,
    qrValue: classicQrValue(invoice, {online: isOnline, url: URL, project: projectName}),
    qrLogo: qrLogoImage(),
    site: WebSite,
    showSignature,
    fontPt: layout.fontPt,
    tight: layout.tight,
  }

  const sheetWidthPx = SHEET_SIZE.landscape.width * PX_PER_MM
  const sheetHeightPx = SHEET_SIZE.landscape.height * PX_PER_MM
  const scale = pdfMode ? 1 : Math.min(1, Math.max(0.2, (viewportWidth - 32) / sheetWidthPx))
  const stackHeight =
    layout.pages.length * sheetHeightPx + Math.max(0, layout.pages.length - 1) * SHEET_GAP_PX
  const payments = invoice.pym || []

  return (
    <>
      <ClassicMeasure
        shared={shared}
        measureRef={measureRef}
        allRowsTotals={allRowsTotals}
        widths={layout.measureWidths}
        probeTotals={probeTotals}
      />

      <main
        className={clsx('cp-viewer', {'cp-viewer--phone-hidden': !fullPrint})}
        aria-label='نسخهٔ چاپی صورتحساب'
        aria-busy={!layout.ready}
      >
        {!layout.ready && !pdfMode && (
          <div className='cp-viewer__busy cp-noprint'>در حال صفحه‌بندی صورتحساب…</div>
        )}
        <div className='cp-viewer__frame' style={{height: stackHeight * scale}}>
          <ClassicSheets
            shared={shared}
            pages={layout.pages}
            widths={layout.widths}
            rowsTotals={layout.rowsTotals}
            sheetsRef={sheetsRef}
            style={{width: sheetWidthPx, transform: `translateX(-50%) scale(${scale})`}}
          />
        </div>
      </main>

      {/* Beside the sheets only: a phone shows the receipt instead, as the earlier page did. */}
      {!fullPrint && !pdfMode && (
        <div className='d-none d-md-block'>
          <div className='cp-noprint cp-options border border-dashed border-primary rounded bg-light-primary m-5 p-5'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-5'>
              <div className='flex-grow-1' style={{minWidth: 260, maxWidth: 760}}>
                <h5 className='mb-2'>چاپ صورتحساب</h5>
                <p className='mb-0 text-gray-700' style={{textAlign: 'justify'}}>
                  {layout.ready
                    ? `صورتحساب روی ${formatNumber(
                        String(layout.pages.length)
                      )} برگهٔ A4 افقی صفحه‌بندی شده است. `
                    : 'صورتحساب در حال صفحه‌بندی روی برگه‌های A4 افقی است. '}
                  هیچ ردیفی بین دو برگه بریده نمی‌شود و وقتی ردیف‌ها بیش از یک برگه‌اند، پای هر برگه
                  «جمع این صفحه» و «جمع کل تا این صفحه» چاپ می‌شود. هنگام چاپ، کاغذ A4 و مقیاس
                  (Scale) ۱۰۰٪ را تغییر ندهید.
                </p>
              </div>
              <div className='d-flex flex-column align-items-center gap-3'>
                <button
                  type='button'
                  className='btn btn-icon btn-light-primary w-60px h-60px'
                  title={layout.ready ? 'چاپ' : 'در حال صفحه‌بندی…'}
                  aria-label='چاپ صورتحساب'
                  disabled={!layout.ready}
                  onClick={() => window.print()}
                >
                  <i className='las la-print fs-2x' aria-hidden='true'></i>
                </button>
                {model.signatureUrl && (
                  <CustomInputs
                    type='toggle'
                    toggleValue={{
                      on: 'همراه با مهر و امضا',
                      off: 'بدون مهر و امضا',
                    }}
                    stateValue={{
                      value: withSign.withSign,
                      setValue: withSign.setWithSign,
                    }}
                  />
                )}
              </div>
            </div>
            {payments.length > 0 && (
              <div className='cp-options__payments'>
                <div className='fw-bold mb-1'>جزئیات پرداخت‌ها</div>
                {payments.map((payment, index) => {
                  const taxValidationError = getPaymentTaxValidationError({
                    ...payment,
                    invoiceNetPrice: invoice.ntp,
                  })
                  return (
                    <div key={payment.id || index} className='cp-options__payment'>
                      <span>
                        <bdi>{payment.dt}</bdi>
                      </span>
                      <span>
                        {
                          InvoicePaymentTypeItems.find((type) => type.value === String(payment.typ))
                            ?.label
                        }
                      </span>
                      <span className='fw-bold'>
                        <bdi dir='ltr'>{formatNumber(String(payment.prc ?? ''))}</bdi> ریال
                      </span>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary py-1'
                        onClick={() => setEditPaymentItem(paymentForm(payment))}
                      >
                        <i className='las la-receipt fs-3' aria-hidden='true'></i>
                        مشاهده
                      </button>
                      {taxValidationError && (
                        <span className='cp-options__payment-warning'>
                          این ردیف پرداخت به سامانه مؤدیان ارسال نمی‌شود: {taxValidationError}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {editPaymentItem !== undefined && (
        <InvoicePaymentEditModal
          paymentItem={{paymentItem: editPaymentItem, setPaymentItem: setEditPaymentItem}}
          isDisabled
        />
      )}
    </>
  )
}

export {InvoicePrintModalPrint}
